import * as React from 'react'
import { Plus, Pencil, Check, X, Trash2, Loader2, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GRUPOS_RESULTADO, GRUPO_RESULTADO_LABEL } from '@/types/estadoResultado'
import { CategoriaGastoBadge } from './CategoriaGastoBadge'
import { CrearCategoriaGastoDialog, type CrearCategoriaGastoData } from './CrearCategoriaGastoDialog'
import {
  useCategoriasGasto,
  useCrearCategoriaGasto,
  useActualizarCategoriaGasto,
  useDesactivarCategoriaGasto,
} from '@/hooks/useGastos'
import type { CategoriaGasto } from '@/services/categoriasGastoService'

interface GestionarCategoriasGastoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const TIPO_LABEL: Record<string, string> = {
  gasto: 'Gasto del estudio',
  retiro: 'Retiro de socios',
  ingreso: 'Ingreso',
}

export function GestionarCategoriasGastoDialog({
  open,
  onOpenChange,
}: GestionarCategoriasGastoDialogProps) {
  const [busqueda, setBusqueda] = React.useState('')
  const [editandoId, setEditandoId] = React.useState<string | null>(null)
  const [nombreEdit, setNombreEdit] = React.useState('')
  const [crearOpen, setCrearOpen] = React.useState(false)
  const [categoriaEliminar, setCategoriaEliminar] = React.useState<CategoriaGasto | null>(null)

  // soloActivas=false → también muestra las desactivadas (para reactivarlas si hiciera falta)
  const { data: categorias = [], isLoading } = useCategoriasGasto(false)
  const crear = useCrearCategoriaGasto()
  const actualizar = useActualizarCategoriaGasto()
  const desactivar = useDesactivarCategoriaGasto()

  const filtradas = React.useMemo(() => {
    if (!busqueda.trim()) return categorias
    const t = busqueda.toLowerCase()
    return categorias.filter((c) => c.nombre.toLowerCase().includes(t))
  }, [categorias, busqueda])

  const iniciarEdicion = (cat: CategoriaGasto) => {
    setEditandoId(cat.id)
    setNombreEdit(cat.nombre)
  }

  const guardarEdicion = async (id: string) => {
    if (!nombreEdit.trim()) return
    await actualizar.mutateAsync({ id, data: { nombre: nombreEdit.trim() } })
    setEditandoId(null)
  }

  const handleCrear = async (data: CrearCategoriaGastoData) => {
    await crear.mutateAsync(data)
  }

  const cambiarGrupo = async (id: string, value: string) => {
    await actualizar.mutateAsync({
      id,
      data: { grupo_resultado: value === 'sin' ? null : value },
    })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg h-[600px] max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Categorías de gasto</DialogTitle>
            <DialogDescription>
              Gestioná las categorías para clasificar los gastos del estudio
            </DialogDescription>
          </DialogHeader>

          {/* Búsqueda + nueva — fijo fuera del scroll */}
          <div className="px-6 pb-3 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar categoría..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button onClick={() => setCrearOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Nueva
            </Button>
          </div>

          {/* Lista scrollable */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filtradas.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {busqueda ? 'No hay categorías que coincidan' : 'No hay categorías. Creá la primera.'}
              </p>
            ) : (
              <div className="space-y-1.5">
                {filtradas.map((cat) => (
                  <div
                    key={cat.id}
                    className={`group flex items-center gap-2 p-2 rounded-lg border ${
                      cat.activa ? 'bg-background' : 'bg-muted/40 opacity-60'
                    }`}
                  >
                    {editandoId === cat.id ? (
                      <>
                        <Input
                          value={nombreEdit}
                          onChange={(e) => setNombreEdit(e.target.value)}
                          className="h-8 flex-1"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') guardarEdicion(cat.id)
                            if (e.key === 'Escape') setEditandoId(null)
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-emerald-600"
                          onClick={() => guardarEdicion(cat.id)}
                          disabled={actualizar.isPending}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          onClick={() => setEditandoId(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <CategoriaGastoBadge nombre={cat.nombre} color={cat.color} className="shrink-0" />
                        <div className="flex-1 min-w-0">
                          {cat.tipo === 'gasto' ? (
                            <Select
                              value={cat.grupo_resultado ?? 'sin'}
                              onValueChange={(v) => cambiarGrupo(cat.id, v)}
                            >
                              <SelectTrigger
                                className={`h-7 text-xs w-48 ${
                                  cat.grupo_resultado
                                    ? ''
                                    : 'text-amber-600 border-amber-300'
                                }`}
                              >
                                <SelectValue placeholder="Sin clasificar" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sin">Sin clasificar</SelectItem>
                                {GRUPOS_RESULTADO.map((g) => (
                                  <SelectItem key={g} value={g}>
                                    {GRUPO_RESULTADO_LABEL[g]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {TIPO_LABEL[cat.tipo] ?? cat.tipo}
                              {!cat.activa && ' · inactiva'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={() => iniciarEdicion(cat)}
                            title="Renombrar"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {cat.activa && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => setCategoriaEliminar(cat)}
                              title="Desactivar"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <CrearCategoriaGastoDialog
        open={crearOpen}
        onOpenChange={setCrearOpen}
        onCrear={handleCrear}
        isCreating={crear.isPending}
      />

      <AlertDialog
        open={!!categoriaEliminar}
        onOpenChange={(o) => !o && setCategoriaEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              La categoría <strong>{categoriaEliminar?.nombre}</strong> dejará de aparecer al
              clasificar gastos nuevos. Los gastos ya clasificados con ella no se modifican.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (categoriaEliminar) {
                  desactivar.mutate(categoriaEliminar.id, {
                    onSuccess: () => setCategoriaEliminar(null),
                  })
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {desactivar.isPending ? 'Desactivando...' : 'Desactivar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
